import { Injectable, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Subscription, firstValueFrom } from 'rxjs';
import { AppState } from 'src/app/app.reducer';
import { AutomaticRateService } from '../../../services/automatic-rate.service';
import { MainRatesService } from '../../../services/main-rates.service';
import { PerfilUsuario } from '../../remittance-hub/models/remittance.model';
import { RemittanceQuoteService } from '../../remittance-hub/services/remittance-quote.service';
import {
  CorredorDef,
  HomeRatesState,
  MonedaMonto,
  TasaHome,
} from '../models/home-rates.model';
import * as R from '../rules/home-rates.rules';
import * as RR from '../../remittance-hub/rules/remittance-rules';

/** Lo único que se guarda por corredor entre ciclos. */
interface ValorCorredor {
  tasa: number | null;
  /** Segundo precio del par. `null` en las filas de un solo número. */
  venta: number | null;
  /** Tramos crudos del riel propio. Se convierten a la vista al emitir. */
  tramos: R.TramoCrudo[] | null;
  /** Tramos VES→USDT (solo la fila USDT del cliente VE). `null` = como antes. */
  tramosUsdt: R.TramoUsdtCrudo[] | null;
  /** Tramos USDT→VES (misma fila). `null` = como antes. */
  tramosUsdtVenta?: R.TramoUsdtCrudo[] | null;
  /** Tramos CLP→USDT (solo la fila USDT del cliente CL logueado). `null` = como antes. */
  tramosClpUsdt?: R.TramoUsdtCrudo[] | null;
  /** `base` de esa respuesta: el back elige el tramo con `montoClp ÷ base`. */
  baseClpUsdt?: number | null;
  /**
   * `precioCorte` del GET de tramos de la compra (VES→USDT o CLP→USDT, 23/10):
   * con él se elige el tramo (`monto ÷ precioCorte`). `null` = como antes.
   */
  precioCorteCompra?: number | null;
  /** Tramos USDT→CLP (misma fila). `null` = como antes. */
  tramosUsdtClp?: R.TramoUsdtCrudo[] | null;
  /**
   * VES→CLP: CLP por VES al mid (`clpPorVesMid` de `ves-clp/ultimas-agrupadas`),
   * el criterio con que el servidor elige la franja. Con él los cortes en CLP
   * se pasan a Bs. (`Bs. = CLP ÷ clpPorVesMid`). Ausente = la tasa A, como antes.
   */
  clpPorVesMid?: number | null;
  actualizadaEn: number | null;
}

/**
 * Motor de la tarjeta de tasas del home.
 *
 * Es `providedIn: 'root'` a propósito: hay UN solo ciclo de refresco para toda
 * la aplicación. Si mañana la tarjeta aparece también en otra pantalla, el
 * tráfico no se duplica — los dos componentes se cuelgan del mismo estado.
 *
 * Tres decisiones que sostienen el resto:
 *
 *  1. **No habla con los ocho endpoints.** Salvo las dos consultas públicas de
 *     tramos y las dos de conversión USDT, todo pasa por
 *     `RemittanceQuoteService.sondear()`, que ya normaliza los diez pares. Su
 *     caché es de 60 s contra un ciclo de 180 s, así que nunca sirve un valor
 *     viejo y de paso amortigua el refresco manual.
 *  2. **Un fallo no borra la fila.** Se conserva el último valor bueno y, si
 *     pasan dos ciclos, se muestra apagado. Blanquear una tasa asusta más que
 *     mostrarla vieja.
 *  3. **Con la pestaña oculta no se pide nada.** Al volver, si el dato pasó un
 *     ciclo, se refresca en el acto.
 *
 * NO toca backend: consume endpoints que ya existen.
 */
@Injectable({ providedIn: 'root' })
export class HomeRatesService implements OnDestroy {
  private readonly estado = new BehaviorSubject<HomeRatesState>({
    perfil: 'CL',
    nivel1: [],
    nivel2: [],
    cargando: false,
    ultimoCicloEn: null,
  });

  readonly estado$ = this.estado.asObservable();

  /** Último valor bueno por corredor. Sobrevive a los ciclos que fallan. */
  private valores = new Map<string, ValorCorredor>();

  private timer: any = null;
  private readonly visHandler = () => this.alCambiarVisibilidad();

  /** Cuántos componentes están mostrando la tarjeta ahora mismo. */
  private consumidores = 0;

  private perfil: PerfilUsuario = 'CL';
  private soloPublicos = false;
  private cicloEnCurso = false;

  /** Hasta que el store no diga de qué país es el cliente, no se pide nada. */
  private perfilResuelto = false;

  /**
   * Turno del perfil vigente. Sube con cada cambio de perfil o de cuenta: un
   * ciclo que arrancó con otro turno ya no es de este cliente y se descarta.
   */
  private turno = 0;

  /** Email de la cuenta con la que se armó el estado. */
  private cuenta: string | null = null;
  private subCuenta: Subscription | null = null;

  constructor(
    private quote: RemittanceQuoteService,
    private mainRates: MainRatesService,
    private automaticRate: AutomaticRateService,
    store?: Store<AppState>,
  ) {
    // El servicio vive lo que vive la pestaña: sin esto, la cuenta que entra
    // después de un cierre de sesión heredaba las tasas de la anterior.
    this.subCuenta =
      store?.select('auth').subscribe((auth: any) => {
        const email: string | null = auth?.user?.email ?? null;
        if (this.cuenta && email !== this.cuenta) this.reiniciar();
        this.cuenta = email;
      }) ?? null;
  }

  // ───────────────────────── Ciclo de vida ─────────────────────────

  /**
   * Engancha un componente al ciclo. Se llama UNA vez por componente.
   *
   * No arranca nada todavía: sin perfil resuelto, pedir tasas es adivinar de
   * qué país es el cliente. El ciclo arranca en `setPerfil`.
   */
  registrar(): void {
    this.consumidores++;
    this.emitir({});
    if (this.perfilResuelto) this.arrancar();
  }

  /**
   * El perfil llega del store y puede llegar después del primer render.
   *
   * Cambiar de perfil descarta los valores: las tasas de un cliente CL no
   * significan nada para uno VE, y mostrarlas un instante sería peor que la
   * tarjeta vacía.
   */
  setPerfil(perfil: PerfilUsuario, soloPublicos: boolean): void {
    const cambio =
      !this.perfilResuelto ||
      perfil !== this.perfil ||
      soloPublicos !== this.soloPublicos;
    if (!cambio) return;

    // Si hay un ciclo en curso, era del perfil anterior: el turno nuevo lo
    // descarta y, al cerrar, arranca el de este perfil.
    this.turno++;
    this.valores.clear();
    this.perfil = perfil;
    this.soloPublicos = soloPublicos;
    this.perfilResuelto = true;
    this.emitir({ perfil, ultimoCicloEn: null });

    if (this.timer) this.refrescar();
    else if (this.consumidores > 0) this.arrancar();
  }

  /**
   * Con qué perfil corre el ciclo, o `null` si nadie lo fijó todavía. Lo lee
   * el asistente para colgarse del ciclo sin pisar lo que decidió el home: si
   * los dos llamaran a `setPerfil` con criterios distintos, se borrarían los
   * valores el uno al otro.
   */
  get configuracion(): { perfil: PerfilUsuario; soloPublicos: boolean } | null {
    return this.perfilResuelto
      ? { perfil: this.perfil, soloPublicos: this.soloPublicos }
      : null;
  }

  /** Suelta un componente. Con el último se apaga el ciclo. */
  soltar(): void {
    this.consumidores = Math.max(0, this.consumidores - 1);
    if (this.consumidores === 0) this.detener();
  }

  ngOnDestroy(): void {
    this.detener();
    this.subCuenta?.unsubscribe();
  }

  /**
   * Vuelve al estado de recién abierta la pestaña. Se llama cuando sale la
   * cuenta o entra otra: nada de lo guardado le sirve a la siguiente.
   */
  private reiniciar(): void {
    this.turno++;
    this.detener();
    this.valores.clear();
    this.perfil = 'CL';
    this.soloPublicos = false;
    this.perfilResuelto = false;
    this.emitir({ perfil: 'CL', cargando: false, ultimoCicloEn: null });
  }

  private arrancar(): void {
    if (this.timer) return;
    document.addEventListener('visibilitychange', this.visHandler);
    this.programar();
    this.refrescar();
  }

  private programar(): void {
    if (this.timer) clearInterval(this.timer);
    this.timer = setInterval(() => this.refrescar(), R.CICLO_MS);
  }

  private detener(): void {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
    document.removeEventListener('visibilitychange', this.visHandler);
  }

  /**
   * Con la pestaña oculta el ciclo se para: son tasas, no una alarma. Al
   * volver, si el dato ya cumplió un ciclo se pide de nuevo antes de que el
   * cliente alcance a leerlo.
   */
  private alCambiarVisibilidad(): void {
    if (!this.timer && this.consumidores === 0) return;

    if (document.hidden) {
      if (this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }
      return;
    }

    this.programar();
    const ultimo = this.estado.value.ultimoCicloEn;
    if (ultimo == null || Date.now() - ultimo >= R.CICLO_MS) this.refrescar();
  }

  // ───────────────────────── Interacción ─────────────────────────

  /** Refresco manual. */
  async refrescar(): Promise<void> {
    if (!this.perfilResuelto || this.cicloEnCurso) return;
    const turno = this.turno;
    this.cicloEnCurso = true;
    this.emitir({ cargando: true });

    try {
      await this.ciclo(turno);
    } finally {
      this.cicloEnCurso = false;
      if (turno === this.turno) {
        this.emitir({ cargando: false, ultimoCicloEn: Date.now() });
      } else {
        // El perfil cambió a mitad de camino: este ciclo no cuenta como
        // terminado y se pide en el acto el del perfil nuevo.
        this.emitir({ cargando: false });
        if (this.timer) this.refrescar();
      }
    }
  }

  // ───────────────────────── Ciclo ─────────────────────────

  private async ciclo(turno: number): Promise<void> {
    const defs = R.corredoresDe(this.perfil, this.soloPublicos);
    const nivel1 = R.deNivel(defs, 1);

    let nivel2: CorredorDef[] = [];

    if (!this.soloPublicos) {
      const activos = await this.destinosConRiel();
      if (turno !== this.turno) return;
      nivel2 = R.deNivel(defs, 2).filter(
        (d) => d.destino != null && activos.has(d.destino),
      );
    }

    // Los destinos que no pasaron el gate se olvidan: si el proveedor apagó
    // Perú, su fila desaparece en vez de quedar con la tasa de ayer.
    this.olvidar(R.deNivel(defs, 2).filter((d) => nivel2.indexOf(d) < 0));

    await Promise.allSettled(
      [...nivel1, ...nivel2].map((def) => this.resolverYGuardar(def, turno)),
    );

    this.emitir({});
  }

  private olvidar(defs: CorredorDef[]): void {
    defs.forEach((d) => this.valores.delete(d.id));
  }

  /**
   * Destinos que se pueden mostrar: las tres puertas juntas.
   *
   * `destinosVitaDisponibles()` ya cubre el flag de servicio y el catálogo
   * activo, y cierra por defecto ante un error. Falta la tercera, que no es
   * del proveedor sino nuestra: `PAISES_CON_RAIL`. El catálogo devuelve los
   * países activos DEL PROVEEDOR, no aquellos a los que Aguacate tiene riel;
   * sin este cruce la tarjeta puede ofrecer una tasa a un país al que después
   * no se puede enviar.
   */
  private async destinosConRiel(): Promise<Set<string>> {
    const r = await this.quote.destinosVitaDisponibles();
    const activos = new Set<string>();
    r.activos.forEach((code) => {
      if (R.tieneRiel(code as any)) activos.add(code);
    });
    return activos;
  }

  /**
   * Un corredor que no resuelve conserva su último valor y su fecha: la vista
   * lo apaga sola cuando pasan dos ciclos.
   */
  private async resolverYGuardar(def: CorredorDef, turno: number): Promise<void> {
    try {
      const v = await this.consultar(def);
      // Llegó tarde: es de un perfil o una cuenta que ya no está.
      if (turno !== this.turno) return;
      if (v.tasa != null && v.tasa > 0) {
        this.valores.set(def.id, {
          tasa: v.tasa * def.base,
          venta: v.venta != null ? v.venta * def.base : null,
          tramos: v.tramos,
          tramosUsdt: v.tramosUsdt ?? null,
          tramosUsdtVenta: v.tramosUsdtVenta ?? null,
          tramosClpUsdt: v.tramosClpUsdt?.tramos ?? null,
          baseClpUsdt: v.tramosClpUsdt?.base ?? null,
          precioCorteCompra: v.tramosClpUsdt?.precioCorte ?? v.precioCorteUsdt ?? null,
          tramosUsdtClp: v.tramosUsdtClp ?? null,
          clpPorVesMid: v.clpPorVesMid ?? null,
          actualizadaEn: Date.now(),
        });
      } else if (!this.valores.has(def.id)) {
        this.valores.set(def.id, this.vacio());
      }
    } catch {
      if (turno !== this.turno) return;
      if (!this.valores.has(def.id)) {
        this.valores.set(def.id, this.vacio());
      }
    }
    this.emitir({});
  }

  private vacio(): ValorCorredor {
    return {
      tasa: null,
      venta: null,
      tramos: null,
      tramosUsdt: null,
      actualizadaEn: null,
    };
  }

  /**
   * Despacho por fuente. Devuelve la tasa POR UNIDAD; la multiplicación por
   * `def.base` —los pares que sólo son legibles por mil— se hace en un solo
   * lugar, arriba.
   */
  private async consultar(def: CorredorDef): Promise<{
    tasa: number | null;
    venta: number | null;
    tramos: R.TramoCrudo[] | null;
    tramosUsdt?: R.TramoUsdtCrudo[] | null;
    /** `precioCorte` de los tramos VES→USDT (23/10). Null = como antes. */
    precioCorteUsdt?: number | null;
    tramosUsdtVenta?: R.TramoUsdtCrudo[] | null;
    tramosClpUsdt?: {
      base: number | null;
      tramos: R.TramoUsdtCrudo[] | null;
      precioCorte?: number | null;
    } | null;
    tramosUsdtClp?: R.TramoUsdtCrudo[] | null;
    clpPorVesMid?: number | null;
  }> {
    // Fila de par: los dos precios del mismo mercado, en paralelo. Si uno de
    // los dos falla, la fila entera se conserva vieja — mostrar sólo la compra
    // sería peor que mostrar las dos de hace un rato.
    if (R.esPar(def)) {
      const [compra, ventaTasa, conCorteUsdt, tramosUsdtVenta, tramosClpUsdt, tramosUsdtClp] = await Promise.all([
        this.precioUsdt(def.fuente),
        this.precioUsdt(def.fuenteVenta as any),
        this.tramosUsdtDe(def),
        this.tramosUsdtVentaDe(def),
        this.tramosClpUsdtDe(def),
        this.tramosUsdtClpDe(def),
      ]);
      return {
        tasa: compra,
        venta: ventaTasa,
        tramos: null,
        tramosUsdt: conCorteUsdt?.tramos ?? null,
        precioCorteUsdt: conCorteUsdt?.precioCorte ?? null,
        tramosUsdtVenta,
        tramosClpUsdt,
        tramosUsdtClp,
      };
    }

    if (def.fuente === 'TRAMOS_CLP_VES' || def.fuente === 'TRAMOS_VES_CLP') {
      const obs =
        def.fuente === 'TRAMOS_CLP_VES'
          ? this.mainRates.obtenerTasasPublicas()
          : this.mainRates.obtenerTasasVesClpPublicas();
      const res: any = await firstValueFrom(obs);
      const tramos = R.tramosDesde(res?.data);
      // Los tres tramos vienen en la MISMA respuesta de la que antes tomaba
      // uno: mostrarlos no cuesta ninguna llamada extra.
      const mid = Number(res?.data?.clpPorVesMid);
      return {
        tasa: R.tasaDeTramos(tramos),
        venta: null,
        tramos,
        clpPorVesMid: def.fuente === 'TRAMOS_VES_CLP' && Number.isFinite(mid) && mid > 0 ? mid : null,
      };
    }

    // COTIZACIÓN: el mismo embudo y el mismo número que muestra el cotizador.
    // En los rieles Vita es la tasa de referencia, no la aplicada: la aplicada
    // trae la comisión adentro y, con el monto chico del sondeo, sale peor que
    // la que el cliente ve después al cotizar.
    const c = await this.quote.sondear(def.origen, def.destino as any);
    return { tasa: RR.tasaMostrada(c), venta: null, tramos: null };
  }

  /**
   * Tramos VES→USDT para la fila USDT del cliente VE logueado (nunca en la
   * vista pública). Con su propio try/catch: si fallan, `null` y la fila se
   * pinta como antes; nunca tumban el par. Trae también el `precioCorte`
   * (23/10) con que se elige el tramo; null si no viene.
   */
  private async tramosUsdtDe(
    def: CorredorDef,
  ): Promise<{ tramos: R.TramoUsdtCrudo[] | null; precioCorte: number | null } | null> {
    if (this.soloPublicos || !R.llevaTramosUsdt(def)) return null;
    try {
      const res: any = await firstValueFrom(this.mainRates.obtenerTramosVesUsdt());
      return { tramos: R.tramosUsdtDesde(res), precioCorte: R.precioCorteDeTramos(res) };
    } catch {
      return null;
    }
  }

  /** Tramos USDT→VES de la misma fila (espejo de `tramosUsdtDe`). Nunca tumban el par. */
  private async tramosUsdtVentaDe(def: CorredorDef): Promise<R.TramoUsdtCrudo[] | null> {
    if (this.soloPublicos || !R.llevaTramosUsdtVenta(def)) return null;
    try {
      const res: any = await firstValueFrom(this.mainRates.obtenerTramosUsdtVes());
      return R.tramosUsdtDesde(res);
    } catch {
      return null;
    }
  }

  /**
   * Tramos CLP→USDT para la fila USDT del cliente CL logueado (nunca en la
   * vista pública). Con su propio try/catch: un backend viejo responde 404/503
   * y la fila se pinta como hoy; nunca tumban el par.
   */
  private async tramosClpUsdtDe(
    def: CorredorDef,
  ): Promise<{ base: number | null; tramos: R.TramoUsdtCrudo[] | null; precioCorte: number | null } | null> {
    if (this.soloPublicos || !R.llevaTramosClpUsdt(def)) return null;
    try {
      const res: any = await firstValueFrom(this.mainRates.obtenerTramosClpUsdt());
      const tramos = R.tramosUsdtDesde(res);
      return tramos ? { base: R.baseDeTramos(res), tramos, precioCorte: R.precioCorteDeTramos(res) } : null;
    } catch {
      return null;
    }
  }

  /** Tramos USDT→CLP de la misma fila (espejo de `tramosClpUsdtDe`). Nunca tumban el par. */
  private async tramosUsdtClpDe(def: CorredorDef): Promise<R.TramoUsdtCrudo[] | null> {
    if (this.soloPublicos || !R.llevaTramosClpUsdt(def)) return null;
    try {
      const res: any = await firstValueFrom(this.mainRates.obtenerTramosUsdtClp());
      return R.tramosUsdtDesde(res);
    } catch {
      return null;
    }
  }

  /** Un precio de USDT. Cada perfil compra y vende contra su moneda local. */
  private async precioUsdt(fuente: string): Promise<number | null> {
    if (fuente === 'USDT_COMPRA') {
      const r: any = await this.automaticRate.getUsdtRateForUser();
      return Number(r?.valoresCambio?.tasaUsdtClp) || null;
    }
    if (fuente === 'USDT_VENTA') {
      const r: any = await this.automaticRate.getUsdtRateSellForUser();
      return Number(r?.valoresCambio?.tasaUsdtClp) || null;
    }
    if (fuente === 'USDT_COMPRA_VES') {
      const r: any = await firstValueFrom(
        this.mainRates.getVesUsdtRateForUser(),
      );
      return (r?.success ? Number(r?.data?.tasaFinal) : null) || null;
    }
    // USDT_VENTA_VES
    const r: any = await firstValueFrom(this.mainRates.getUsdtVesRateForUser());
    return (r?.success ? Number(r?.data?.tasaFinal) : null) || null;
  }

  // ───────────────────────── Estado ─────────────────────────

  private emitir(parcial: Partial<HomeRatesState>): void {
    const previo = this.estado.value;
    const defs = R.corredoresDe(this.perfil, this.soloPublicos);

    this.estado.next({
      ...previo,
      ...parcial,
      perfil: parcial.perfil ?? previo.perfil,
      nivel1: this.filasDe(R.deNivel(defs, 1)),
      nivel2: this.filasDe(R.deNivel(defs, 2)),
    });
  }

  /** Las filas van en el orden de la definición, no en el de respuesta. */
  private filasDe(defs: CorredorDef[]): TasaHome[] {
    const ahora = Date.now();
    const filas: TasaHome[] = [];

    defs.forEach((def) => {
      const v = this.valores.get(def.id);
      if (!v) return;
      filas.push({
        def,
        tasa: v.tasa,
        venta: v.venta,
        // VES→CLP: los pisos en Bs. con el mid, la misma base que la escalera.
        tramos: R.tramosParaVista(def, v.tramos, this.baseVesDe(def, v)),
        tramosUsdt: R.tramosUsdtParaVista(v.tramosUsdt),
        tramosUsdtVenta: R.tramosUsdtVentaParaVista(v.tramosUsdtVenta),
        ...this.escalerasDe(def, v),
        vieja: R.estaVencida(v.actualizadaEn, ahora),
        actualizadaEn: v.actualizadaEn,
      });
    });

    return filas;
  }

  /**
   * Escaleras con la forma única `EscalonVista`. Aditivo: los campos de
   * siempre (`tramos`, `tramosUsdt`, `tramosUsdtVenta`) siguen saliendo igual
   * porque los lee el asistente.
   */
  private escalerasDe(
    def: CorredorDef,
    v: ValorCorredor,
  ): Pick<TasaHome, 'escalera' | 'escaleraVenta' | 'baseEscalera' | 'precioCorteEscalera'> {
    // Riel propio: cortes en CLP; el cliente VE escribe en Bs. Con 1 franja
    // (o todas iguales) no hay escalera: la fila queda como hoy.
    if (v.tramos && v.tramos.length) {
      const monedaMonto = this.monedaMontoDe(def);
      const baseVes = this.baseVesDe(def, v);
      return {
        escalera: R.escaleraDesde(
          v.tramos.map((t) => ({ desde: t.pisoClp, tasa: t.tasa })),
          {
            unidadCorte: 'CLP',
            monedaMonto,
            tipoAhorro: def.tipoAhorro || (def.mejorEs === 'MENOR' ? 'PAGA_MENOS' : 'RECIBE_MAS'),
            base: baseVes,
            exigirMejora: true,
          },
        ),
        escaleraVenta: null,
        baseEscalera: baseVes,
      };
    }
    if (!R.esPar(def) || this.soloPublicos) {
      return { escalera: null, escaleraVenta: null, baseEscalera: null };
    }
    // Par USDT: cortes en USDT. Solo con al menos 2 tramos que mejoren algo.
    const compra = R.llevaTramosClpUsdt(def) ? v.tramosClpUsdt : v.tramosUsdt;
    const venta = R.llevaTramosClpUsdt(def) ? v.tramosUsdtClp : v.tramosUsdtVenta;
    const base = R.llevaTramosClpUsdt(def) ? v.baseClpUsdt ?? null : null;
    const local = def.monedaMonto || 'CLP';
    return {
      escalera: R.escaleraDesde(compra, {
        unidadCorte: 'USDT',
        monedaMonto: local,
        tipoAhorro: def.tipoAhorro || 'PAGA_MENOS',
        exigirMejora: true,
      }),
      escaleraVenta: R.escaleraDesde(venta, {
        unidadCorte: 'USDT',
        monedaMonto: def.ladoVenta?.monedaMonto || 'USDT',
        tipoAhorro: def.ladoVenta?.tipoAhorro || 'RECIBE_MAS',
        exigirMejora: true,
      }),
      baseEscalera: base,
      // La compra se elige con `monto ÷ precioCorte` si el GET lo trae (23/10); si no, como hoy.
      precioCorteEscalera: v.precioCorteCompra ?? null,
    };
  }

  /** Moneda en la que el cliente escribe el monto de la fila. */
  private monedaMontoDe(def: CorredorDef): MonedaMonto {
    return def.monedaMonto || (def.origen === 'VES' ? 'VES' : 'CLP');
  }

  /**
   * Base para rotular en Bs. los cortes en CLP del riel VES→CLP: Bs. por CLP =
   * 1 ÷ `clpPorVesMid`, porque el servidor elige la franja con los CLP al mid.
   * `null` sin el dato (backend viejo) o si el monto no se escribe en Bs.: ahí
   * manda la tasa del primer tramo, como antes. La usan la escalera y la lista
   * del asistente (`tramosParaVista`), para que digan el mismo corte.
   */
  private baseVesDe(def: CorredorDef, v: ValorCorredor): number | null {
    if (this.monedaMontoDe(def) !== 'VES') return null;
    const mid = Number(v.clpPorVesMid);
    return Number.isFinite(mid) && mid > 0 ? 1 / mid : null;
  }
}
